import c from './image-gallery.module.scss';
import { useState, useRef } from 'react';
import {
  Modal,
  ModalSection,
  IconButton,
  Icon,
  Button,
} from '@monash/portal-react';
import { useApi, getFeedbackAttachments } from '@monash/portal-frontend-common';
import * as Sentry from '@sentry/browser';

const ImageGallery = ({ feedbackItem, selectedFeedbackId }) => {
  // Modal control
  const [imageIndex, setImageIndex] = useState(0);
  const [isShown, setIsShown] = useState(false);
  const triggerRef = useRef();
  const openMenu = (index) => {
    setImageIndex(index);
    setIsShown(true);
  };

  const closeMenu = () => {
    setIsShown(false);
    triggerRef.current.focus();
  };

  const sortList = (listToSort) => {
    const images = listToSort.filter((item) => isImage(item));
    const files = listToSort.filter((item) => !isImage(item));
    const sortedAttachments = [...images, ...files];
    return sortedAttachments;
  };

  const { data: getAttachmentResponse } = useApi({
    requestFn: async (feedbackItem, selectedFeedbackId) => {
      if (feedbackItem?.hasAttachment) {
        const returnItems = await getFeedbackAttachments(selectedFeedbackId);
        return sortList(returnItems);
      }
    },
    args: [feedbackItem, selectedFeedbackId],
    skipInitialCall: false,
    onError: (error) => {
      Sentry.captureException(error);
    },
  });

  // swiper no swiping
  const swipeImage = (increment) => {
    const totalImages = getAttachmentResponse?.length;
    setImageIndex((prevIndex) => {
      const newIndex = prevIndex + increment;
      if (newIndex === totalImages) return 0;
      if (newIndex < 0) return totalImages - 1;
      return newIndex;
    });
  };

  const imageName = (index) => {
    if (getAttachmentResponse && getAttachmentResponse[index]?.name) {
      return getAttachmentResponse[index]?.name.split('/').pop();
    }
  };

  const isImage = (item) => {
    return ['image/jpeg', 'image/png', 'image/tiff', 'image/webp'].includes(
      item.contentType
    );
  };

  const handleKeyDown = (e, index) => {
    if (e.code === 'Enter') {
      openMenu(index);
    }
  };

  const imageGallery = feedbackItem?.hasAttachment ? (
    <div className={c.gallery}>
      {getAttachmentResponse?.map((item, index) => {
        if (isImage(item)) {
          return (
            <img
              key={item.name}
              alt={imageName(index)}
              className={c.smallImage}
              src={`${item.url}`}
              onClick={() => openMenu(index)}
              tabIndex="0"
              onKeyDown={(e) => handleKeyDown(e, index)}
            />
          );
        } else {
          return (
            <a
              key={item.name}
              className={c.link}
              href={item.url}
              target="_blank"
              rel="noreferrer"
            >
              {imageName(index)} <Icon.FileDownload />
            </a>
          );
        }
      })}
    </div>
  ) : (
    <div>N/A</div>
  );

  const openLink = (url) => {
    window.open(url, '_blank');
  };

  return (
    <div>
      <Modal
        open={isShown}
        onClose={closeMenu}
        dismissOnHistoryNav={true}
        ariaLabel="Image Gallery"
        ariaDescribedby={null}
      >
        <ModalSection
          title={imageName(imageIndex)}
          titleTabIndex={null}
          ariaLabelledby="imageGalleryModalTitle"
          ariaDescribedby="imageGalleryModalContent"
        >
          <div className={c.attachmentDisplay}>
            {getAttachmentResponse?.length > 1 ? (
              <IconButton
                size={24}
                icon={Icon.ChevronLeft}
                onClick={() => swipeImage(-1)}
                ref={triggerRef}
                mode="canvas"
                aria-expanded={isShown}
                aria-label="Previous attachment"
                aria-haspopup="dialog"
              />
            ) : null}

            <div className={c.middleChunk}>
              {getAttachmentResponse &&
              getAttachmentResponse[imageIndex] &&
              isImage(getAttachmentResponse[imageIndex]) ? (
                <img
                  className={c.bigImage}
                  src={getAttachmentResponse[imageIndex].url}
                  alt="Attachment"
                />
              ) : null}

              <div>
                <Button
                  icon={<Icon.FileDownload />}
                  variant="primary"
                  onClick={() => {
                    openLink(getAttachmentResponse[imageIndex].url);
                  }}
                >
                  {imageName(imageIndex)}
                </Button>
              </div>
            </div>

            {getAttachmentResponse?.length > 1 ? (
              <div>
                <IconButton
                  size={24}
                  icon={Icon.ChevronRight}
                  onClick={() => swipeImage(+1)}
                  ref={triggerRef}
                  mode="canvas"
                  aria-expanded={isShown}
                  aria-label="Next attachment"
                  aria-haspopup="dialog"
                />
              </div>
            ) : null}
          </div>
        </ModalSection>
      </Modal>

      <div>{imageGallery}</div>
    </div>
  );
};

export default ImageGallery;
